import React, { useEffect, useMemo, useRef } from "react";
import FormItem from "@components/FormItem/FormItem";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import { usePersistentForm } from "@hooks/usePersistentForm";
import type { ReportGeneralInfo } from "@services/drybulkCargoOperationsApi";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { GENERAL_INFO_FORM_PERSIST_KEY } from "../constants";
import { useGeneralInfoFormStore } from "../hooks/useGeneralInfoFormStore";
import { useGatedOpen } from "../hooks/usePollingGate";
import { useUpdateGeneralInfo } from "../hooks/useUpdateGeneralInfo";
import { problemMessage } from "../utils/problemDetail";
import {
  GENERAL_INFO_FIELDS as FIELDS,
  generalInfoDefaultValues,
  generalInfoSchema,
  L_GENERAL_INFO as L,
  type GeneralInfoFormData,
} from "./generalInfoSchema";

const text = (value: number | null | undefined) =>
  value === null || value === undefined ? "" : String(value);

/**
 * DBCO-0059 (R-31, AD-97) - Modify: Water Consumption (m3) and Max Feed Rate (MT/hour) in a side drawer, prefilled from the report.
 * The schema mirrors the server's 400 rules; the server stays authoritative and a refusal is shown with its reason while the drawer
 * keeps what was entered. The draft is kept per operation and cleared on Cancel and on success.
 */
const GeneralInfoDrawer: React.FC<{
  operationId: string;
  info: ReportGeneralInfo;
  onClose: () => void;
}> = ({ operationId, info, onClose }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  useGatedOpen(true);
  const mutation = useUpdateGeneralInfo(operationId);
  const storedOperationId = useGeneralInfoFormStore((state) => state.operationId);
  const storedWater = useGeneralInfoFormStore((state) => state.waterConsumption);
  const storedFeed = useGeneralInfoFormStore((state) => state.maxFeedRate);
  const setStored = useGeneralInfoFormStore((state) => state.setValues);
  const resetStore = useGeneralInfoFormStore((state) => state.reset);
  const schema = useMemo(() => generalInfoSchema(t), [t]);
  const defaults = useMemo<GeneralInfoFormData>(
    () => ({
      ...generalInfoDefaultValues,
      waterConsumption: text(info.waterConsumption),
      maxFeedRate: text(info.maxFeedRate),
    }),
    [info.waterConsumption, info.maxFeedRate],
  );
  const storeValues = useMemo(
    () =>
      storedOperationId === operationId
        ? { waterConsumption: storedWater, maxFeedRate: storedFeed }
        : {},
    [storedOperationId, operationId, storedWater, storedFeed],
  );
  const form = usePersistentForm<GeneralInfoFormData>({
    schema,
    defaultValues: defaults,
    storeValues,
    setStoreValues: (values) =>
      setStored(operationId, values.waterConsumption ?? "", values.maxFeedRate ?? ""),
    resetStore,
    persistKey: GENERAL_INFO_FORM_PERSIST_KEY,
  });
  const { watch } = form;
  const discarded = useRef(false);

  useEffect(() => {
    if (storedOperationId !== null && storedOperationId !== operationId) {
      resetStore();
    }
  }, [storedOperationId, operationId, resetStore]);

  useEffect(() => {
    const subscription = watch((values) => {
      if (!discarded.current) {
        setStored(operationId, values.waterConsumption ?? "", values.maxFeedRate ?? "");
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setStored, operationId]);

  const discardDraft = () => {
    discarded.current = true;
    form.clear();
    resetStore();
  };

  const onValid = async (values: GeneralInfoFormData) => {
    try {
      await mutation.mutateAsync({
        waterConsumption: Number(values.waterConsumption),
        maxFeedRate: Number(values.maxFeedRate),
      });
      notify({ type: "success", title: t(`${L}.saved`) });
      discardDraft();
      onClose();
    } catch (error) {
      notify({
        type: "error",
        title: t("common.error"),
        description: problemMessage(error) ?? t(`${L}.save-failed`),
      });
    }
  };

  const onInvalid = () =>
    notify({
      type: "error",
      title: t(`${L}.validation-error-title`),
      description: t(`${L}.validation-error-description`),
    });

  return (
    <SideDrawer
      isOpen
      title={t(`${L}.title`)}
      onClose={onClose}
      onCancel={() => {
        discardDraft();
        onClose();
      }}
      onSubmit={form.handleSubmit(onValid, onInvalid)}
      isSubmitting={mutation.isPending}
      submitLabel={t(`${L}.submit`)}
      cancelLabel={t(`${L}.cancel`)}
      width={480}
    >
      <div data-testid="general-info-drawer" className="w-full flex flex-col gap-2 px-6 py-2">
        <FormProvider {...form}>
          <FormItem<GeneralInfoFormData, "waterConsumption">
            required
            name={FIELDS.WATER_CONSUMPTION}
            type="text"
            label={t(`${L}.fields.water`)}
            testId="general-info-water"
            slotProps={{ htmlInput: { inputMode: "decimal" } }}
          />
          <FormItem<GeneralInfoFormData, "maxFeedRate">
            required
            name={FIELDS.MAX_FEED_RATE}
            type="text"
            label={t(`${L}.fields.feed-rate`)}
            testId="general-info-feed-rate"
            slotProps={{ htmlInput: { inputMode: "numeric" } }}
          />
        </FormProvider>
      </div>
    </SideDrawer>
  );
};

export default GeneralInfoDrawer;
